import React, { useCallback, useEffect, useMemo } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { hoursToMilliseconds } from 'date-fns';

import RecordIcon from 'assets/images/svgs/Recording/ico_record.svg';
import RecordIconAll from 'assets/images/svgs/Recording/ico_record_all.svg';

import {
    CANCEL_EPISODE_DESCRIPTION,
    CANCEL_EPISODE_DIALOG_TITLE,
    CANCEL_EPISODE_NO_BUTTON,
    CANCEL_EPISODE_YES_BUTTON,
    CANCEL_SERIES_DESCRIPTION,
    CANCEL_SERIES_DIALOG_TITLE,
    CANCEL_SERIES_NO_BUTTON,
    CANCEL_SERIES_YES_BUTTON,
    DELETE_EPISODE_DESCRIPTION,
    DELETE_EPISODE_DIALOG_TITLE,
    DELETE_EPISODE_NO_BUTTON,
    DELETE_EPISODE_YES_BUTTON,
    DELETE_SERIES_DESCRIPTION,
    DELETE_SERIES_DIALOG_TITLE,
    DELETE_SERIES_NO_BUTTON,
    DELETE_SERIES_YES_BUTTON,
    LOADING_SPINNER_ID,
} from 'config/testID';

import { selectAppState, selectLayoutConfig } from 'store/config/selectors';
import { getDeleteActionPinValidationTime } from 'store/recordings/selectors.ts';
import { ReduxStore } from 'store/types.ts';
import { selectActiveUserProfile } from 'store/user/selectors.ts';

import isFunction from 'utils/function';
import { UseRecordingsHookReturnType } from 'utils/hooks/useRecordings';
import { isTabletOrWeb, isWeb } from 'utils/responsiveness';

import {
    BUTTON_NO,
    SCREEN_ALERT_CANCEL_EPISODE_BODY,
    SCREEN_ALERT_CANCEL_EPISODE_CANCEL_BUTTON,
    SCREEN_ALERT_CANCEL_EPISODE_TITLE,
    SCREEN_ALERT_CANCEL_SERIES_BODY,
    SCREEN_ALERT_CANCEL_SERIES_CANCEL_BUTTON,
    SCREEN_ALERT_CANCEL_SERIES_TITLE,
    SCREEN_ALERT_CANCEL_SINGLE_RECORDING_BODY,
    SCREEN_ALERT_CANCEL_SINGLE_RECORDING_TITLE,
    SCREEN_ALERT_DELETE_EPISODE_RECORDING_BODY,
    SCREEN_ALERT_DELETE_EPISODE_RECORDING_DELETE_BUTTON,
    SCREEN_ALERT_DELETE_EPISODE_RECORDING_TITLE,
    SCREEN_ALERT_DELETE_RECORDING_BODY,
    SCREEN_ALERT_DELETE_RECORDING_DELETE_BUTTON,
    SCREEN_ALERT_DELETE_RECORDING_DELETE_SERIES_BUTTON,
    SCREEN_ALERT_DELETE_RECORDING_TITLE,
    SCREEN_ALERT_DELETE_SERIES_RECORDING_BODY,
    SCREEN_ALERT_DELETE_SERIES_RECORDING_TITLE,
    SCREEN_ALERT_QUOTA_EXCEEDED_BODY,
    SCREEN_ALERT_QUOTA_EXCEEDED_TITLE,
    SCREEN_AUDIO_SUBTITLES_TITLE_GOT_IT_BUTTON,
    SERIES_NOTIFICATIONS,
} from 'screens/Detail/languageConst';
import { PinDialogType } from 'screens/Detail/types';

import Notification from 'components/Notification';
import PinDialog, { PinDialogRef } from 'components/PinDialog';
import { PinDialogStatus } from 'components/PinDialog/hooks/getPinDialogText';
import LoadingCircle from 'components/Spinner';

import RecordingsDialog from './components/RecordingsDialog';
import styles from './style';

type Props = {
    isRecordingLoading: UseRecordingsHookReturnType['isRecordingLoading'];
    seriesId: UseRecordingsHookReturnType['seriesId'];
    handleCancelRecording: UseRecordingsHookReturnType['handleCancelRecording'];
    handleCancelSeriesRecording: UseRecordingsHookReturnType['handleCancelSeriesRecording'];
    recordingsNotification: UseRecordingsHookReturnType['recordingsNotification'];
    showQuotaDialog: UseRecordingsHookReturnType['showQuotaDialog'];
    showCancelEpisodeDialog: UseRecordingsHookReturnType['showCancelEpisodeDialog'];
    showCancelSeriesDialog: UseRecordingsHookReturnType['showCancelSeriesDialog'];
    showDeleteRecordingDialog?: UseRecordingsHookReturnType['showDeleteRecordingDialog'];
    showDeleteSeriesDialog?: UseRecordingsHookReturnType['showDeleteSeriesDialog'];
    resetNotificationCallback: UseRecordingsHookReturnType['resetNotificationCallback'];
    hideCancelEpisodeDialogCallback: UseRecordingsHookReturnType['hideCancelEpisodeDialogCallback'];
    hideCancelSeriesDialogCallback: UseRecordingsHookReturnType['hideCancelSeriesDialogCallback'];
    hideQuotaDialogCallback: UseRecordingsHookReturnType['hideQuotaDialogCallback'];
    hideDeleteSeriesDialogCallback?: UseRecordingsHookReturnType['hideDeleteSeriesDialogCallback'];
    hideDeleteRecordingDialogCallback?: UseRecordingsHookReturnType['hideDeleteRecordingDialogCallback'];
    handleShowPinDialog?: (pinType: PinDialogType) => void;

    pinDialogProps: {
        pinDialogRef: React.RefObject<PinDialogRef | null>;
        showPinDialog: boolean;
        handlePinDialogClose: () => void;
        handlePinDialogSubmit: (pin: string) => void;
        pinDialogStatus: PinDialogStatus;
    };
    fromPlayer?: boolean;
    playerInFullscreen?: boolean;
    showDropdown?: UseRecordingsHookReturnType['showRecordingDropdown'];
    recordingIsProtected: boolean;
    seriesHasProtectedRecording: boolean;
    onDeleteRecording(pin?: string): void;
    onDeleteSeriesRecording(pin?: string): void;
    onOpenDetails?(): void;
};

const PIN_SAFE_TIME_FOR_DELETE_ACTION = hoursToMilliseconds(24);

function RecordingsNotificationsAndOverlay({
    // recording data
    isRecordingLoading,
    // series data
    seriesId,
    // handlers
    handleCancelRecording,
    handleCancelSeriesRecording,
    // dialogs and notifications
    recordingsNotification,
    showQuotaDialog,
    showCancelEpisodeDialog,
    showCancelSeriesDialog,
    showDeleteRecordingDialog,
    showDeleteSeriesDialog,
    resetNotificationCallback,
    hideCancelEpisodeDialogCallback,
    hideCancelSeriesDialogCallback,
    hideDeleteRecordingDialogCallback,
    hideDeleteSeriesDialogCallback,
    hideQuotaDialogCallback,
    handleShowPinDialog,
    pinDialogProps,
    fromPlayer,
    playerInFullscreen,
    showDropdown,
    recordingIsProtected,
    seriesHasProtectedRecording,
    onDeleteRecording,
    onDeleteSeriesRecording,
    onOpenDetails,
}: Props) {
    const { t } = useTranslation();
    const appState = useSelector(selectAppState);
    const { primary } = useSelector(selectLayoutConfig);
    const selectedProfileId = useSelector(selectActiveUserProfile);

    const deleteActionPinValidationTime = useSelector((state: ReduxStore) =>
        getDeleteActionPinValidationTime(state, selectedProfileId),
    );

    const {
        pinDialogRef,
        showPinDialog,
        handlePinDialogClose,
        handlePinDialogSubmit,
        pinDialogStatus,
    } = pinDialogProps;

    const isSeriesNotification = useMemo(() => {
        if (recordingsNotification) {
            return SERIES_NOTIFICATIONS.some((notification) => {
                return t(notification) === recordingsNotification.title;
            });
        }

        return showCancelSeriesDialog;
    }, [recordingsNotification, showCancelSeriesDialog, t]);

    const handlePinOverlayButtonClose = useCallback(() => {
        handlePinDialogClose();

        if (isWeb && !fromPlayer) {
            window.history.back();
        }
    }, [handlePinDialogClose, fromPlayer]);

    const handleBackButtonPinOverlay = useCallback(() => {
        if (showPinDialog) {
            handlePinDialogClose();
        }
    }, [showPinDialog, handlePinDialogClose]);

    const handleBackButton = useCallback(
        (event?: PopStateEvent) => {
            if (isWeb && event) {
                window.history.pushState({}, '', null);
            }

            if (showCancelSeriesDialog) {
                setTimeout(() => {
                    hideCancelSeriesDialogCallback();
                }, 100);
            } else if (showCancelEpisodeDialog) {
                setTimeout(() => {
                    hideCancelEpisodeDialogCallback();
                }, 100);
            } else if (
                showDeleteSeriesDialog &&
                isFunction(hideDeleteSeriesDialogCallback)
            ) {
                hideDeleteSeriesDialogCallback();
            } else if (
                showDeleteRecordingDialog &&
                isFunction(hideDeleteRecordingDialogCallback)
            ) {
                hideDeleteRecordingDialogCallback();
            } else if (showQuotaDialog && isFunction(hideQuotaDialogCallback)) {
                setTimeout(() => {
                    hideQuotaDialogCallback();
                }, 100);
            }
        },
        [
            showCancelSeriesDialog,
            showCancelEpisodeDialog,
            showDeleteSeriesDialog,
            hideDeleteSeriesDialogCallback,
            showDeleteRecordingDialog,
            hideDeleteRecordingDialogCallback,
            showQuotaDialog,
            hideQuotaDialogCallback,
            hideCancelSeriesDialogCallback,
            hideCancelEpisodeDialogCallback,
        ],
    );

    useEffect(() => {
        if (
            isWeb &&
            !fromPlayer &&
            (showPinDialog ||
                showCancelSeriesDialog ||
                showCancelEpisodeDialog ||
                showDeleteRecordingDialog ||
                showDeleteSeriesDialog ||
                showQuotaDialog)
        ) {
            if (showPinDialog) {
                setTimeout(() => {
                    window.history.pushState({}, '', null);
                    window.addEventListener(
                        'popstate',
                        handleBackButtonPinOverlay,
                        { once: true },
                    );
                }, 100);
            } else {
                window.addEventListener('popstate', handleBackButton);
            }
        }

        return () => {
            if (isWeb) {
                window.removeEventListener('popstate', handleBackButton);
            }

            resetNotificationCallback();
        };
    }, [
        showPinDialog,
        showCancelSeriesDialog,
        showCancelEpisodeDialog,
        showDeleteRecordingDialog,
        showDeleteSeriesDialog,
        handleBackButtonPinOverlay,
        handleBackButton,
        fromPlayer,
        showQuotaDialog,
        resetNotificationCallback,
    ]);

    const deleteEpisodeOnPress = useCallback(() => {
        if (
            recordingIsProtected ||
            !deleteActionPinValidationTime ||
            Date.now() - deleteActionPinValidationTime >
                PIN_SAFE_TIME_FOR_DELETE_ACTION
        ) {
            hideDeleteRecordingDialogCallback?.();
            handleShowPinDialog?.(PinDialogType.DELETE);
        } else {
            onDeleteRecording();
            hideDeleteRecordingDialogCallback?.();
            onOpenDetails?.();
        }
    }, [
        recordingIsProtected,
        deleteActionPinValidationTime,
        hideDeleteRecordingDialogCallback,
        handleShowPinDialog,
        onDeleteRecording,
        onOpenDetails,
    ]);

    const deleteSeriesOnPress = useCallback(() => {
        if (
            seriesHasProtectedRecording ||
            !deleteActionPinValidationTime ||
            Date.now() - deleteActionPinValidationTime >
                PIN_SAFE_TIME_FOR_DELETE_ACTION
        ) {
            hideDeleteSeriesDialogCallback?.();
            handleShowPinDialog?.(PinDialogType.DELETE_SERIES);
        } else {
            hideDeleteSeriesDialogCallback?.();
            onDeleteSeriesRecording();
            onOpenDetails?.();
        }
    }, [
        deleteActionPinValidationTime,
        handleShowPinDialog,
        hideDeleteSeriesDialogCallback,
        seriesHasProtectedRecording,
        onDeleteSeriesRecording,
        onOpenDetails,
    ]);

    const NotificationIcon = useCallback(() => {
        return isSeriesNotification ? (
            <RecordIconAll height={40} width={40} />
        ) : (
            <RecordIcon height={40} width={40} />
        );
    }, [isSeriesNotification]);

    const NotificationComponent = useCallback(() => {
        return (
            <Notification
                notification={recordingsNotification}
                onResetNotification={resetNotificationCallback}
                customIcon={
                    recordingsNotification?.withCustomIcon ? (
                        <NotificationIcon />
                    ) : undefined
                }
                fromPlayer={fromPlayer}
            />
        );
    }, [
        NotificationIcon,
        fromPlayer,
        recordingsNotification,
        resetNotificationCallback,
    ]);

    return (
        <>
            {isRecordingLoading && (
                <LoadingCircle
                    style={styles.loading}
                    testID={LOADING_SPINNER_ID}
                    size={isTabletOrWeb ? 48 : 32}
                    routeKey=""
                    color={primary}
                />
            )}
            {recordingsNotification && <NotificationComponent />}
            {/** Cancel episode recording dialog */}
            {showCancelEpisodeDialog && (
                <RecordingsDialog
                    title={
                        !seriesId
                            ? t(SCREEN_ALERT_CANCEL_SINGLE_RECORDING_TITLE)
                            : t(SCREEN_ALERT_CANCEL_EPISODE_TITLE)
                    }
                    description={
                        !seriesId
                            ? t(SCREEN_ALERT_CANCEL_SINGLE_RECORDING_BODY)
                            : t(SCREEN_ALERT_CANCEL_EPISODE_BODY)
                    }
                    actionButtonTitle={
                        SCREEN_ALERT_CANCEL_EPISODE_CANCEL_BUTTON
                    }
                    handleActionButtonPress={handleCancelRecording}
                    actionButtonFillColor={primary}
                    actionButtonTextStyle={styles.cancelButtonText}
                    cancelButtonTitle={BUTTON_NO}
                    handleCancelPress={hideCancelEpisodeDialogCallback}
                    cancelButtonBordered
                    fromPlayer={fromPlayer}
                    actionButtonStyle={
                        fromPlayer && [styles.button, styles.actionButton]
                    }
                    cancelButtonStyle={fromPlayer && styles.button}
                    style={styles.dialogBackground}
                    titleTestID={CANCEL_EPISODE_DIALOG_TITLE}
                    descriptionTestID={CANCEL_EPISODE_DESCRIPTION}
                    actionButtonTestID={CANCEL_EPISODE_YES_BUTTON}
                    closeButtonTestID={CANCEL_EPISODE_NO_BUTTON}
                />
            )}
            {/** Cancel series recording dialog */}
            {showCancelSeriesDialog && (
                <RecordingsDialog
                    title={t(SCREEN_ALERT_CANCEL_SERIES_TITLE)}
                    description={t(SCREEN_ALERT_CANCEL_SERIES_BODY)}
                    actionButtonTitle={SCREEN_ALERT_CANCEL_SERIES_CANCEL_BUTTON}
                    handleActionButtonPress={handleCancelSeriesRecording}
                    actionButtonFillColor={primary}
                    actionButtonTextStyle={styles.cancelButtonText}
                    cancelButtonTitle={BUTTON_NO}
                    handleCancelPress={hideCancelSeriesDialogCallback}
                    cancelButtonBordered
                    actionButtonStyle={
                        fromPlayer && [styles.button, styles.actionButton]
                    }
                    cancelButtonStyle={fromPlayer && styles.button}
                    style={styles.dialogBackground}
                    fromPlayer={fromPlayer}
                    titleTestID={CANCEL_SERIES_DIALOG_TITLE}
                    descriptionTestID={CANCEL_SERIES_DESCRIPTION}
                    actionButtonTestID={CANCEL_SERIES_YES_BUTTON}
                    closeButtonTestID={CANCEL_SERIES_NO_BUTTON}
                />
            )}
            {/** Delete episode recording dialog */}
            {showDeleteRecordingDialog && (
                <RecordingsDialog
                    title={
                        seriesId
                            ? t(SCREEN_ALERT_DELETE_EPISODE_RECORDING_TITLE)
                            : t(SCREEN_ALERT_DELETE_RECORDING_TITLE)
                    }
                    description={
                        seriesId
                            ? t(SCREEN_ALERT_DELETE_EPISODE_RECORDING_BODY)
                            : t(SCREEN_ALERT_DELETE_RECORDING_BODY)
                    }
                    actionButtonTitle={
                        seriesId
                            ? SCREEN_ALERT_DELETE_EPISODE_RECORDING_DELETE_BUTTON
                            : SCREEN_ALERT_DELETE_RECORDING_DELETE_BUTTON
                    }
                    handleActionButtonPress={deleteEpisodeOnPress}
                    actionButtonFillColor={primary}
                    actionButtonTextStyle={styles.cancelButtonText}
                    cancelButtonTitle={BUTTON_NO}
                    handleCancelPress={hideDeleteRecordingDialogCallback}
                    cancelButtonBordered
                    actionButtonStyle={
                        fromPlayer && [styles.button, styles.actionButton]
                    }
                    cancelButtonStyle={fromPlayer && styles.button}
                    style={styles.dialogBackground}
                    fromPlayer={fromPlayer}
                    showDropdown={showDropdown}
                    titleTestID={DELETE_EPISODE_DIALOG_TITLE}
                    descriptionTestID={DELETE_EPISODE_DESCRIPTION}
                    actionButtonTestID={DELETE_EPISODE_YES_BUTTON}
                    closeButtonTestID={DELETE_EPISODE_NO_BUTTON}
                />
            )}
            {/** Delete series recording dialog */}
            {showDeleteSeriesDialog && (
                <RecordingsDialog
                    title={t(SCREEN_ALERT_DELETE_SERIES_RECORDING_TITLE)}
                    description={t(SCREEN_ALERT_DELETE_SERIES_RECORDING_BODY)}
                    actionButtonTitle={
                        SCREEN_ALERT_DELETE_RECORDING_DELETE_SERIES_BUTTON
                    }
                    handleActionButtonPress={deleteSeriesOnPress}
                    actionButtonFillColor={primary}
                    actionButtonTextStyle={styles.cancelButtonText}
                    cancelButtonTitle={BUTTON_NO}
                    handleCancelPress={hideDeleteSeriesDialogCallback}
                    cancelButtonBordered
                    actionButtonStyle={
                        fromPlayer && [styles.button, styles.actionButton]
                    }
                    cancelButtonStyle={fromPlayer && styles.button}
                    style={styles.dialogBackground}
                    fromPlayer={fromPlayer}
                    showDropdown={showDropdown}
                    titleTestID={DELETE_SERIES_DIALOG_TITLE}
                    descriptionTestID={DELETE_SERIES_DESCRIPTION}
                    actionButtonTestID={DELETE_SERIES_YES_BUTTON}
                    closeButtonTestID={DELETE_SERIES_NO_BUTTON}
                />
            )}
            {/** Quota error dialog */}
            {showQuotaDialog && (
                <RecordingsDialog
                    title={t(SCREEN_ALERT_QUOTA_EXCEEDED_TITLE)}
                    description={t(SCREEN_ALERT_QUOTA_EXCEEDED_BODY)}
                    descriptionNumberOfLines={0}
                    errorCode="A014"
                    actionButtonTitle={t(
                        SCREEN_AUDIO_SUBTITLES_TITLE_GOT_IT_BUTTON,
                    )}
                    handleActionButtonPress={hideQuotaDialogCallback}
                    handleCancelPress={hideQuotaDialogCallback}
                    actionButtonFillColor={primary}
                    actionButtonTextStyle={styles.cancelButtonText}
                    actionButtonStyle={
                        fromPlayer && [styles.button, styles.actionButton]
                    }
                    style={styles.dialogBackground}
                    fromPlayer={fromPlayer}
                />
            )}
            {showPinDialog && (
                <View style={styles.dialogBackground}>
                    <PinDialog
                        ref={pinDialogRef}
                        appState={appState}
                        visible={showPinDialog}
                        onClose={handlePinOverlayButtonClose}
                        onSubmit={handlePinDialogSubmit}
                        status={pinDialogStatus}
                        fromPlayer={fromPlayer}
                        modal={
                            !isWeb ||
                            (isWeb && fromPlayer && !playerInFullscreen)
                        }
                        haveToShowCloseButton
                        style={styles.pinBackground}
                    />
                </View>
            )}
        </>
    );
}

export default RecordingsNotificationsAndOverlay;
